/* global debounce */

if (!customElements.get('predictive-search')) {
  // Brand typo corrections for the predictive dropdown only. The full /search
  // page spell-corrects natively on submit; the predictive API is typo-strict
  // and would return nothing for these. Mined from the Doofinder query-log
  // export (2026-07-28..2026-08-11); every target is a stocked brand or
  // matching product term. Misspellings of brands we don't carry (shimano,
  // phenix, calstar...) are deliberately absent - "correcting" them would
  // still return zero results.
  const SEARCH_TYPO_MAP = {
    diawa: 'daiwa',
    flourocarbon: 'fluorocarbon',
    florocarbon: 'fluorocarbon',
    flurocarbon: 'fluorocarbon',
    fluorcabor: 'fluorocarbon',
    flouro: 'fluorocarbon',
    fluro: 'fluorocarbon',
    floro: 'fluorocarbon',
    flour: 'fluorocarbon',
    tungston: 'tungsten',
    tunsten: 'tungsten',
    tungsen: 'tungsten',
    thungst: 'tungsten',
    tungstwn: 'tungsten',
    tungasten: 'tungsten',
    tingsten: 'tungsten',
    ccurate: 'accurate',
    acurate: 'accurate',
    accurte: 'accurate',
    abet: 'avet',
    avit: 'avet',
    aver: 'avet',
    avrt: 'avet',
    avets: 'avet',
    avent: 'avet',
    avey: 'avet',
    wvet: 'avet',
    avetex80: 'avet',
    salsa: 'salas',
    salad: 'salas',
    rspalas: 'salas',
    salsasal: 'salas',
    kuma: 'okuma',
    ocuma: 'okuma',
    okima: 'okuma',
    okam: 'okuma',
    okumo: 'okuma',
    rapalla: 'rapala',
    taddy: 'tady',
    peen: 'penn',
    prnn: 'penn',
    normad: 'nomad',
    faltfall: 'flat fall',
    // Compound-word fixes from the Doofinder synonym worksheet (2026-08-13):
    // carried products the native tokenizer misses when the space is dropped.
    // Brand-level synonyms (shimano->okuma etc.) are deliberately NOT here -
    // they redirect via the search-routes menu on submit, where the full
    // results page makes the substitution visible; silently swapping brands
    // inside the predictive dropdown would be confusing.
    luckycraft: 'lucky craft',
    momoiande: 'momoi ande',
    fishpliers: 'fish pliers',
    leadheadgrub: 'lead head grub',
    hxjcedros: 'avet hxj',
    // Round 3 (2026-09-10 four-week search review): tokens seen >=3x in the
    // GA4 / Shopify search logs. The map is applied per token, so multi-word
    // misspellings are added as their misspelled token ("yahoo bomb" corrects
    // to "wahoo bomb" via yahoo->wahoo, then searches natively). "ave thx" ->
    // "avet hx" was deliberately left out: it would need ave->avet, too broad
    // a token to remap safely.
    whaoo: 'wahoo',
    yahoo: 'wahoo',
    avett: 'avet',
    zaragoza: 'saragosa',
    makira: 'makaira',
    salitga: 'saltiga',
    seguar: 'seaguar',
    seagar: 'seaguar',
    sibiki: 'sabiki',
    isoline: 'izorline',
    okim: 'okuma',
    phoenix: 'phenix',
    shiman: 'shimano',
    rapture: 'raptor',
    yamaha: 'yamaga',
    maurder: 'marauder',
    coltsniper: 'colt sniper',
    coltsnip: 'colt sniper',
    oceajigger: 'ocea jigger',
    meister: 'meekster',
    zuckerberg: 'zukers'
  };

  // OPP-0092: brands to strip from the front of a query when the whole query is
  // not itself a route key. Deliberately explicit rather than "any route key":
  // "shimano" and "power pro" are the confirmed cases where a model-qualified
  // form ("shimano talica", "power pro maxcuatro") should follow the model's
  // route. Carried own-page brands like "avet" are excluded, so "avet reel
  // cover" keeps its own explicit route and "rod holder" never strips (its
  // remainder is not a route key).
  const BRAND_PREFIX_STRIP = ['shimano', 'power pro'];

  // OPP-0146: since about September 2026 the predictive API reads a plain
  // query for its meaning and ignores resources[options][fields], so a search
  // for a brand can miss that brand's products ("big hammer" returned ten jig
  // heads and none of the nine Big Hammer listings; "nomad" three of 35 Nomad
  // Design products). A vendor:"..." query still returns exactly the brand,
  // but on its own it drops the query suggestions and collections. So a query
  // that names a stocked brand keeps its normal panel, and when that panel's
  // products are not all the brand's, its product cards are refilled from a
  // second, vendor-filtered request (withBrandProducts). These trailing words
  // are dropped from a vendor name to get the short form shoppers type
  // ("Nomad Design" -> "nomad", "Avet Reels" -> "avet").
  const VENDOR_NAME_SUFFIXES = [
    'fishing',
    'lures',
    'reels',
    'rods',
    'design',
    'designs',
    'hooks',
    'tackle',
    'baits',
    'co.',
    'company',
    'products',
    'industries',
    'enterprises',
    'sports',
    'boots',
    'jigs',
    'blanks',
    'irons',
    'usa'
  ];

  class PredictiveSearch extends HTMLElement {
    static RECENT_KEY = 'cb-recent-searches';

    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.quickLinks = this.querySelector('.js-search-quick-links');
      this.loadingText = this.getAttribute('data-loading-text');

      // OPP-0043: keyword routing table (query -> destination URL), rendered
      // from the "search-routes" menu. Malformed or absent = routing off.
      this.searchRoutes = null;
      const routesEl = this.querySelector('[data-search-routes]');
      if (routesEl) {
        try {
          this.searchRoutes = JSON.parse(routesEl.textContent);
        } catch (e) {
          this.searchRoutes = null;
        }
      }

      // OPP-0146: brand queries (normalized query -> vendor names), built from
      // shop.vendors. Absent or malformed = no brand handling, as before.
      this.brandQueries = new Map();
      const vendorsEl = this.querySelector('[data-search-vendors]');
      if (vendorsEl) {
        try {
          this.brandQueries = PredictiveSearch.buildBrandQueries(
            JSON.parse(vendorsEl.textContent),
            (name) => this.normalizeQuery(name)
          );
        } catch (e) {
          this.brandQueries = new Map();
        }
      }

      // Bail on childless upgrades (e.g. cloneNode/re-parse by browser features
      // or extensions) — there is nothing to wire up and child access would throw.
      if (!this.input || !this.overlay) return;

      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      if (!this.input || !this.overlay) return;
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      // A click on an ALREADY-focused input fires no focus event, so after an
      // Escape-close (which keeps the cursor in the box) a re-click wouldn't
      // reopen the layer. handleFocus is safe to re-run while open.
      this.input.addEventListener('click', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));

      const clearRecentBtn = this.querySelector('.js-clear-recent');
      if (clearRecentBtn) {
        clearRecentBtn.addEventListener('click', () => {
          try {
            localStorage.removeItem(PredictiveSearch.RECENT_KEY);
          } catch (e) {
            // Storage unavailable - nothing to clear.
          }
          this.renderRecentSearches();
        });
      }
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
        return;
      }

      const query = this.getQuery();
      this.saveRecentSearch(query);

      // Keyword routing: a mapped query lands on its curated collection page
      // instead of /search. ref/sq params tag the navigation for GA4 without
      // splitting session attribution (not utm_*); Shopify ignores unknown
      // params and the collection's canonical stays clean.
      // Own-property lookup only: on a plain object, queries like
      // "constructor" would surface inherited members and break the submit.
      const normalized = this.normalizeQuery(query);
      let route = this.searchRoutes
        && Object.prototype.hasOwnProperty.call(this.searchRoutes, normalized)
        ? this.searchRoutes[normalized]
        : null;
      // OPP-0092: exact match wins; otherwise try stripping a known brand
      // prefix ("shimano talica" -> talica's route, "power pro maxcuatro" ->
      // maxcuatro's route). Unmatched queries still fall through to /search.
      if (!route) route = this.resolveBrandStrippedRoute(normalized);
      if (route) {
        evt.preventDefault();
        const joiner = route.includes('?') ? '&' : '?';
        window.location.assign(`${route}${joiner}ref=search-route&sq=${encodeURIComponent(query)}`);
      }
    }

    /**
     * Normalizes a query for matching: lowercase, collapsed whitespace,
     * known brand typos corrected token-by-token.
     * @param {string} term - Search query as typed.
     * @returns {string}
     */
    normalizeQuery(term) {
      return term
        .toLowerCase()
        .split(/\s+/)
        .map((token) => SEARCH_TYPO_MAP[token] || token)
        .join(' ')
        .trim();
    }

    /**
     * OPP-0092: brand-stripping fallback. If a normalized query is not an exact
     * route but begins with a strippable brand (BRAND_PREFIX_STRIP) and the
     * remainder is itself a route key, return the remainder's destination.
     * Narrow by design so it never fires for carried own-page brands or for a
     * remainder that is not a route key.
     * @param {string} normalized - Output of normalizeQuery.
     * @returns {string|null}
     */
    resolveBrandStrippedRoute(normalized) {
      if (!this.searchRoutes) return null;
      for (const brand of BRAND_PREFIX_STRIP) {
        const prefix = `${brand} `;
        if (normalized.startsWith(prefix)) {
          const remainder = normalized.slice(prefix.length).trim();
          if (
            remainder
            && Object.prototype.hasOwnProperty.call(this.searchRoutes, remainder)
          ) {
            return this.searchRoutes[remainder];
          }
        }
      }
      return null;
    }

    /**
     * OPP-0146: the queries that name a stocked brand exactly - each vendor's
     * full name and, when it ends in a generic word, its short form - mapped
     * to the vendor names they stand for.
     * @param {Array} vendors - shop.vendors.
     * @param {function(string): string} normalize - Query normalizer, so both
     *   sides of the comparison are normalized the same way.
     * @returns {Map<string, string[]>}
     */
    static buildBrandQueries(vendors, normalize) {
      const queries = new Map();
      if (!Array.isArray(vendors)) return queries;
      const add = (query, vendor) => {
        if (!queries.has(query)) queries.set(query, []);
        queries.get(query).push(vendor);
      };
      vendors.forEach((vendor) => {
        // A quote would break the vendor:"..." query.
        if (typeof vendor !== 'string' || vendor.includes('"')) return;
        const full = normalize(vendor);
        if (!full) return;
        add(full, vendor);

        const tokens = full.split(' ');
        while (tokens.length > 1 && VENDOR_NAME_SUFFIXES.includes(tokens[tokens.length - 1])) {
          tokens.pop();
        }
        const short = tokens.join(' ');
        // A bare number ("13 Fishing" -> "13") is not a brand on its own.
        if (short !== full && short.length >= 3 && /[a-z]/.test(short)) add(short, vendor);
      });
      return queries;
    }

    /**
     * OPP-0146: the vendors a query names, or null when it is not exactly a
     * stocked brand. The product-type filter composes its own query, so a
     * filtered search gets no brand handling.
     * @param {string} term - Output of normalizeQuery.
     * @returns {?string[]}
     */
    brandVendors(term) {
      if (this.productTypeInput && this.productTypeInput.value !== '') return null;
      return (this.brandQueries && this.brandQueries.get(term)) || null;
    }

    /**
     * OPP-0146: for a query that names a stocked brand, make sure the product
     * cards are that brand's. A panel whose products are already all the
     * brand's and fill the limit is returned untouched. Otherwise, if the
     * vendor-filtered request finds more of the brand's products, the panel
     * keeps its suggestions, collections and the brand cards it already had
     * (in its own order), and the rest of its product cards are replaced from
     * the brand request.
     * @param {string} markup - Predictive panel markup for the plain query.
     * @param {string[]} vendors - Output of brandVendors.
     * @param {Promise<?string>} brandRequest - getBrandResults(vendors),
     *   started alongside the plain request so a refill adds no wait.
     * @returns {Promise<string>} - The markup to render.
     */
    async withBrandProducts(markup, vendors, brandRequest) {
      const panelId = `${this.input.id}-products`;
      const cardsIn = (root) => {
        const panel = root.getElementById(panelId);
        return panel ? [...panel.querySelectorAll('.predictive-search__item--card')] : [];
      };
      const productId = (card) => {
        const el = card.querySelector('[data-product-id]');
        return el ? el.getAttribute('data-product-id') : null;
      };

      const tmpl = document.createElement('template');
      tmpl.innerHTML = markup;
      const cards = cardsIn(tmpl.content);
      const brandCards = cards.filter((card) => vendors.includes(card.dataset.vendor));
      const limit = Number(theme.settings.pSearchLimit) || 10;
      if (cards.length >= limit && brandCards.length === cards.length) return markup;

      const brandMarkup = await brandRequest;
      if (!brandMarkup) return markup;
      const brandTmpl = document.createElement('template');
      brandTmpl.innerHTML = brandMarkup;
      const fetched = cardsIn(brandTmpl.content);
      if (fetched.length <= brandCards.length) return markup;

      // No product tab to refill (suggestions or collections only): show the
      // brand's products instead.
      const list = cards.length ? cards[0].parentElement : null;
      if (!list) return brandMarkup;

      const kept = new Set(brandCards.map(productId));
      const merged = brandCards
        .concat(fetched.filter((card) => !kept.has(productId(card))))
        .slice(0, limit);
      const viewAll = list.querySelector('.js-submit');
      cards.forEach((card) => card.remove());
      merged.forEach((card) => list.insertBefore(document.importNode(card, true), viewAll));
      return tmpl.innerHTML;
    }

    /**
     * OPP-0146: the predictive panel for a vendor-filtered query. Best effort:
     * any failure returns null and the plain panel stands.
     * @param {string[]} vendors - Vendor names.
     * @returns {Promise<?string>} - Panel markup, or null.
     */
    async getBrandResults(vendors) {
      const query = vendors.map((vendor) => `vendor:"${vendor}"`).join(' OR ');
      let params = `q=${encodeURIComponent(query)}`;
      params += `&${encodeURIComponent('resources[type]')}=product`;
      params += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      params += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      params += `&${encodeURIComponent('resources[options][unavailable_products]')}=last`;
      params += '&section_id=predictive-search';

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`);
        if (!response.ok) return null;
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        return resultsEl ? resultsEl.innerHTML.replace(/psearch/g, this.input.id) : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Saves a submitted search term to the device-local recent searches.
     * @param {string} term - Search query as typed.
     */
    saveRecentSearch(term) {
      // handleSubmit already blocks empty queries; guard anyway so no future
      // caller can store a blank chip.
      if (!term) return;
      try {
        let recent = [];
        try {
          recent = JSON.parse(localStorage.getItem(PredictiveSearch.RECENT_KEY)) || [];
        } catch (e) {
          recent = [];
        }
        if (!Array.isArray(recent)) recent = [];
        recent = [
          term,
          ...recent.filter((t) => typeof t === 'string' && t.toLowerCase() !== term.toLowerCase())
        ].slice(0, 8);
        localStorage.setItem(PredictiveSearch.RECENT_KEY, JSON.stringify(recent));
      } catch (e) {
        // Storage unavailable (private mode etc.) - recents just don't persist.
      }
    }

    /**
     * Renders the recent searches chips into the quick links panel.
     * @returns {boolean} - Whether any recent searches were rendered.
     */
    renderRecentSearches() {
      const group = this.quickLinks && this.quickLinks.querySelector('.js-recent-searches');
      if (!group) return false;

      let recent = [];
      try {
        recent = JSON.parse(localStorage.getItem(PredictiveSearch.RECENT_KEY)) || [];
      } catch (e) {
        recent = [];
      }
      // Drop malformed entries (non-strings, empties) so hand-edited or
      // corrupted storage can't render blank chips.
      if (!Array.isArray(recent)) recent = [];
      recent = recent.filter((t) => typeof t === 'string' && t.trim());

      if (!recent.length) {
        group.hidden = true;
        return false;
      }

      const list = group.querySelector('.js-recent-searches-list');
      list.textContent = '';
      recent.forEach((term) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'predictive-search__quick-link link';
        link.href = `${(theme.routes && theme.routes.search) || '/search'}?q=${encodeURIComponent(term)}`;
        link.textContent = term;
        li.appendChild(link);
        list.appendChild(li);
      });
      group.hidden = false;
      return true;
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        if (!this.openQuickLinks()) this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      // Set when the reset button fully closes the layer and hands focus
      // back to the input - that programmatic focus must not reopen it.
      if (this.skipNextFocusOpen) {
        this.skipNextFocusOpen = false;
        return;
      }

      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.openQuickLinks();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Opens the pre-typing quick links panel, if the theme rendered one
     * (it only exists when the "search-quick-links" menu has links).
     * @returns {boolean} - Whether the panel exists.
     */
    openQuickLinks() {
      if (!this.quickLinks) return false;

      // Skip opening when there is nothing to show (no saved searches and no
      // menu-driven groups).
      const hasRecent = this.renderRecentSearches();
      const hasMenuGroups = !!this.quickLinks.querySelector(
        '.predictive-search__quick-group:not(.js-recent-searches)'
      );
      if (!hasRecent && !hasMenuGroups) return false;

      this.quickLinks.hidden = false;
      this.setAttribute('default-open', '');
      // The results listbox is hidden while the panel shows, even if results
      // were rendered for an earlier query.
      this.input.setAttribute('aria-expanded', 'false');
      this.open(true);
      return true;
    }

    /**
     * Hides the pre-typing quick links panel.
     */
    hideQuickLinks() {
      if (!this.quickLinks) return;
      this.quickLinks.hidden = true;
      this.removeAttribute('default-open');
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        case 'Backspace':
          // On touch devices, a backspace with the field already empty closes
          // the layer and drops the keyboard so the shopper is back on the
          // page (keydown fires before deletion, so deleting the last
          // character does NOT close - the empty-state panel shows instead).
          if (!this.getQuery().length && window.matchMedia('(pointer: coarse)').matches) {
            this.close();
            this.input.blur();
          }
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, show the quick links panel
      // (falling back to closing the results). Without this the keyup close
      // races the debounced input handler and the panel flickers.
      if (!this.getQuery().length) {
        if (!this.openQuickLinks()) this.close();
      }
    }

    /**
     * Collapses the on-screen keyboard when a touch user starts scrolling
     * the results layer (the input otherwise keeps focus and the keyboard
     * covers half the results). Desktop pointers are left alone so wheel
     * scrolling doesn't steal focus mid-typing. Blurring does not close the
     * layer - only Escape, reset and overlay clicks do.
     * @param {object} evt - Scroll event (captured; scroll doesn't bubble).
     */
    handleResultsScroll(evt) {
      if (document.activeElement !== this.input) return;
      if (!window.matchMedia('(pointer: coarse)').matches) return;
      // Keep the keyboard while browsing the pre-typing panel, and while
      // swiping a horizontal chip row (their scroll target is the list, not
      // the scroller) - only a vertical scroll of the results collapses it.
      if (this.hasAttribute('default-open')) return;
      if (evt.target.classList && evt.target.classList.contains('predictive-search__scroller')) {
        this.input.blur();
      }
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      // The predictive panel groups results into tabpanels; the OPP-0062
      // relaxed fallback is a single ungrouped grid with no tablist. Scope to
      // the visible tabpanel when there is one, otherwise to the whole results
      // container, so arrow keys reach the fallback cards too.
      const scope = this.querySelector('[role="tabpanel"]:not([hidden])') || this.results;
      if (!scope) return;

      const selectedResult = scope.querySelector('[aria-selected="true"]');
      const allResults = scope.querySelectorAll('.predictive-search__item');
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      // Product results are full cards (their link is .js-prod-link, not a
      // direct child); other result types keep the .js-search-link anchor.
      const selectedResult = this.querySelector(
        '[aria-selected="true"] .js-search-link, [aria-selected="true"] .js-prod-link'
      );
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      // Rewrite known brand misspellings token-by-token (the visible input is
      // left as typed; submitting still sends the raw query to /search, where
      // native spell correction applies).
      const correctedTerm = this.normalizeQuery(searchTerm);

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(correctedTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(correctedTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      // Sold-out products last, independent of the Search & Discovery
      // "Out of stock products" setting (OPP-0068 item 2).
      searchParams += `&${encodeURIComponent('resources[options][unavailable_products]')}=last`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      // Each call takes a ticket. A slower request whose ticket has since been
      // superseded by a later keystroke must not paint stale results - which
      // matters more now that an empty result can cost a second round trip.
      this.requestTicket = (this.requestTicket || 0) + 1;
      const ticket = this.requestTicket;

      // OPP-0146: a query naming a stocked brand also asks for that brand's
      // products, in parallel; it never rejects (getBrandResults).
      const brandVendors = this.brandVendors(correctedTerm);
      const brandRequest = brandVendors ? this.getBrandResults(brandVendors) : null;

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        let resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        if (brandVendors) {
          resultsMarkup = await this.withBrandProducts(resultsMarkup, brandVendors, brandRequest);
          if (ticket !== this.requestTicket) return;
        }

        // OPP-0062: the predictive endpoint ANDs every token and never spell
        // corrects, so compound and multi-word queries come back empty while
        // the results page has plenty ("overhead reels": 0 here, 297 there).
        // Rather than show an empty panel, re-ask the results-page engine.
        if (!PredictiveSearch.markupHasResults(resultsMarkup)) {
          const relaxed = await this.getRelaxedResults(correctedTerm);
          if (ticket !== this.requestTicket) return;
          if (relaxed) resultsMarkup = relaxed;
        }

        if (ticket !== this.requestTicket) return;

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Asks the full results page for the same query through the Section
     * Rendering API, so the dropdown falls back to the engine that actually
     * answers it (OR semantics, last-token prefix matching, native spell
     * correction). Best effort: any failure returns null and the empty panel
     * stands, exactly as it does today.
     * @param {string} term - Already typo-normalized search query.
     * @returns {Promise<?string>} - Results markup, or null when there is none.
     */
    async getRelaxedResults(term) {
      // If the route table redirects this query on submit, preview where the
      // shopper will actually land rather than a relaxed search for a brand
      // we don't stock. "hookup" is the case that caught this: Enter goes to
      // the Ultralight Heavyweight substitute, while an unaware fallback
      // offered tuna hooks - the dropdown disagreeing with submit is the very
      // failure this whole change exists to remove.
      let effectiveTerm = term;
      const route = this.searchRoutes
        && Object.prototype.hasOwnProperty.call(this.searchRoutes, term)
        ? this.searchRoutes[term]
        : null;
      if (route) {
        const routedQuery = route.match(/[?&]q=([^&]*)/);
        if (routedQuery) {
          effectiveTerm = decodeURIComponent(routedQuery[1].split('+').join(' '));
        }
      }

      let query = effectiveTerm;
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        query = `product_type:${this.productTypeInput.value} AND ${effectiveTerm}`;
      }

      const params = `q=${encodeURIComponent(
        query
      )}&type=product&${encodeURIComponent('options[unavailable_products]')}=last&section_id=predictive-search-fallback`;

      try {
        const response = await fetch(`${theme.routes.search}?${params}`);
        if (!response.ok) return null;

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        // Section Rendering wraps the section in an id'd div; fall back to the
        // panel itself if that wrapper is ever absent.
        const wrapper = tmpl.content.querySelector('#shopify-section-predictive-search-fallback');
        let markup = '';
        if (wrapper) {
          markup = wrapper.innerHTML;
        } else {
          const panel = tmpl.content.querySelector('.predictive-search');
          markup = panel ? panel.outerHTML : '';
        }

        return PredictiveSearch.markupHasResults(markup) ? markup : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Whether a results markup string holds at least one real result. The
     * "view all" row is a submit affordance, not a result.
     * @param {string} markup - Results markup.
     * @returns {boolean}
     */
    static markupHasResults(markup) {
      if (!markup) return false;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = markup;

      // Query suggestions ("did you mean") share the item class but are not
      // an answer - a panel holding one suggestion chip and no products is
      // still the empty state this fallback exists to replace.
      const items = tmpl.content.querySelectorAll('.predictive-search__item:not(.js-submit)');
      return Array.prototype.some.call(
        items,
        (item) => !item.closest('.predictive-result__list-suggestions')
      );
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      // No aria-hidden toggling: un-hiding and setting text in the same frame
      // races the accessibility tree and the announcement is never voiced
      // (caught by a real NVDA pass 2026-08-12). A visually-hidden role=status
      // region announces its text changes without any hiding dance.
      this.statusEl.textContent = statusText;
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.hideQuickLinks();
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
      this.announceResultCount();
    }

    /**
     * Announces the number of rendered results in the live region.
     */
    announceResultCount() {
      const allItems = this.results.querySelectorAll('.predictive-search__item:not(.js-submit)');
      const hiddenItems = this.results.querySelectorAll(
        '[role="tabpanel"][hidden] .predictive-search__item:not(.js-submit)'
      );
      const count = allItems.length - hiddenItems.length;

      let statusText;
      if (count === 0) {
        statusText = this.getAttribute('data-results-none-text');
      } else if (count === 1) {
        statusText = this.getAttribute('data-results-one-text');
      } else {
        statusText = (this.getAttribute('data-results-other-text') || '').replace('[count]', count);
      }
      if (statusText) this.setLiveRegionText(statusText);
    }

    /**
     * Measures the geometry the layer CSS positions itself with: the search
     * form's viewport offset and the usable page width (so the layer can
     * bleed full page width on desktop), and the header's bottom edge (so
     * the layer opens below the header instead of painting over the logo).
     * The width is window.innerWidth minus the scrollbar width captured
     * while the scroll lock is off - documentElement.clientWidth cannot be
     * used here because under the lock it counts the reserved scrollbar
     * gutter as client area, reporting ~15px wider than the unlocked page.
     */
    setLayerGeometry() {
      const searchForm = this.querySelector('.search');
      if (!searchForm) return;
      if (!document.body.classList.contains('overlay-predictive-search')) {
        this.scrollbarComp = window.innerWidth - document.documentElement.clientWidth;
      }
      const formRect = searchForm.getBoundingClientRect();
      this.style.setProperty('--ps-form-left', `${Math.round(formRect.left)}px`);
      this.style.setProperty('--ps-page-width', `${window.innerWidth - (this.scrollbarComp || 0)}px`);

      const header = this.closest('.header');
      if (header) {
        const headerBottom = header.getBoundingClientRect().bottom;
        this.style.setProperty('--ps-top-offset', `${Math.round(headerBottom - formRect.top)}px`);
        this.style.setProperty('--ps-header-bottom', `${Math.round(headerBottom)}px`);
      }
    }

    /**
     * Opens the results listbox (or, with quickLinksOnly, just the layer -
     * the quick links panel is plain navigation, not the combobox popup, so
     * aria-expanded stays false until real results render).
     * @param {boolean} [quickLinksOnly] - Opening for the quick links panel.
     */
    open(quickLinksOnly) {
      // Geometry is frozen for the whole open-session: open() runs again on
      // every results render, by then the scroll lock is active and a fresh
      // measurement differs from the pre-lock one, so re-measuring made the
      // layer's width depend on interaction history (empty panel vs typed
      // results vs cleared). Only a real window resize re-measures.
      if (!this.hasAttribute('open')) this.setLayerGeometry();

      this.overlay.classList.add('is-visible');
      // The reset X also shows on the pre-typing panel (empty query) - there
      // it closes the layer instead of clearing text.
      if (this.getQuery().length || quickLinksOnly) this.resetBtn.hidden = false;
      if (!quickLinksOnly) this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);
      this.scrollBlurHandler = this.scrollBlurHandler || this.handleResultsScroll.bind(this);
      // The mobile menu drawer opens beneath the search overlay (z-index 900
      // on mobile) and the body scroll lock, so a hamburger tap looked dead
      // while search was open - close the search first and let the menu's
      // own handler open the drawer normally.
      this.menuToggleClickHandler = this.menuToggleClickHandler || ((evt) => {
        if (evt.target.closest && evt.target.closest('.main-menu__toggle')) this.close();
      });
      this.resizeHandler = this.resizeHandler || (() => {
        if (this.hasAttribute('open')) this.setLayerGeometry();
      });

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
      this.addEventListener('scroll', this.scrollBlurHandler, true);
      document.addEventListener('click', this.menuToggleClickHandler, true);
      window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // The reset button is two-stage: with text it clears back to the
      // empty-state panel; already empty, it closes the layer fully and
      // hands focus back to the input without reopening.
      const resetClicked = evt && evt.target === this.resetBtn;
      if (resetClicked && this.getQuery().length) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
        if (this.openQuickLinks()) return;
        // No panel to fall back to - continue into a full close.
      }
      if (resetClicked) {
        this.skipNextFocusOpen = true;
        // Belt and braces: if the focus event never fires (input already
        // focused), don't leave the flag armed to swallow a real focus.
        setTimeout(() => {
          this.skipNextFocusOpen = false;
        }, 100);
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.hideQuickLinks();
      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening. The keyup
      // listener was historically leaked here - with the quick-links panel
      // reopening on empty-field keyup, that leak made Escape (keydown
      // closes, keyup fired the reopen) look broken.
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
      this.removeEventListener('scroll', this.scrollBlurHandler, true);
      document.removeEventListener('click', this.menuToggleClickHandler, true);
      window.removeEventListener('resize', this.resizeHandler);

      if (resetClicked && !this.getQuery().length) this.input.focus();
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
